import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

// Common weak passwords to flag (subset focused on this app's defaults)
const WEAK_DEFAULTS = ['admin2025', 'demo2025!', 'password', '123456', 'admin', 'azerty'];

const computeStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: 'bg-slate-200', textColor: 'text-slate-500' };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (WEAK_DEFAULTS.includes(pw.toLowerCase())) score = 1;
  const levels = [
    { label: 'Très faible', color: 'bg-rose-500', textColor: 'text-rose-600' },
    { label: 'Faible', color: 'bg-rose-400', textColor: 'text-rose-500' },
    { label: 'Moyen', color: 'bg-amber-400', textColor: 'text-amber-600' },
    { label: 'Bon', color: 'bg-emerald-400', textColor: 'text-emerald-600' },
    { label: 'Fort', color: 'bg-emerald-600', textColor: 'text-emerald-700' },
  ];
  return { score, ...levels[Math.min(score, 4)] };
};

const ChangePasswordModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => computeStrength(next), [next]);
  const isUsingDefault = WEAK_DEFAULTS.includes((current || '').toLowerCase());

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNext(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast({ title: 'Erreur', description: 'Les nouveaux mots de passe ne correspondent pas.', variant: 'destructive' });
      return;
    }
    if (next.length < 6) {
      toast({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 6 caractères.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast({ title: 'Mot de passe modifié', description: 'Votre nouveau mot de passe est actif immédiatement.' });
      close();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de modifier le mot de passe.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        data-testid="change-password-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative"
        >
          <button
            onClick={close}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] p-8 pb-12">
            <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
              <ShieldCheck className="h-7 w-7 text-white" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 -mt-6 space-y-4">
            <div>
              <h2 className="text-2xl font-bold text-[#2C3C65]">Modifier le mot de passe</h2>
              <p className="text-sm text-[#6777A1] mt-1">
                Pour des raisons de sécurité, votre mot de passe actuel est requis.
              </p>
            </div>

            {isUsingDefault && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="default-pwd-warning">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Mot de passe par défaut détecté</p>
                  <p className="text-xs text-amber-800">
                    Vous utilisez actuellement un mot de passe trop simple. Choisissez un mot de passe fort et unique avant la mise en production.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Mot de passe actuel</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                  className="pl-10 pr-10"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 pr-10"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNext(!showNext)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-[#6777A1] mt-1">Minimum 6 caractères</p>
              {/* Strength bar */}
              {next && (
                <div className="mt-2" data-testid="password-strength">
                  <div className="flex gap-1 mb-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength.score ? strength.color : 'bg-slate-200'}`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${strength.textColor}`}>
                    Force : {strength.label}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Confirmer le nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type={showNext ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="pl-10"
                  data-testid="confirm-password"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={close} className="flex-1">
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#285BBB] hover:bg-[#1e47a0] text-white"
                data-testid="submit-change-password"
              >
                {loading ? 'Enregistrement...' : 'Modifier'}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChangePasswordModal;
